/* eslint-disable simple-import-sort/imports, import/order */
import { Box, HStack, VStack } from '@chakra-ui/react';
import FullCalendar, { EventInput } from '@fullcalendar/react';
import jaLocale from '@fullcalendar/core/locales/ja'; // locale
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction'; // click
/* eslint-enable simple-import-sort/imports, import/order */

import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useSearchParams } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { ListUtils } from '../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../components/Check';
import { BackButton, DecideButton } from '../../../components/StyledButton';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import {
  DateChangerState,
  generateInitialState,
  useDateChangers,
} from '../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { usePostDeadlineSearch } from '../../../hooks/queries/deadline';
import {
  SHIFT_APPLY_CONTEXT_NAMES,
  usePostShiftApply,
  usePostShiftApplySearch,
} from '../../../hooks/queries/shiftApply';
import useToastHook from '../../../hooks/ToastHook';
import {
  DailyShift,
  DeadlineMode,
  MasterKey,
  ShiftApplySingleSearchResult,
} from '../../../models/gen/models';
import { Formatter } from '../../../utils/FormatUtils';
import { useMasterContext } from '../../MasterProvider';
import { useSizeContext } from '../../SizeProvider';
import Wireframe from '../../wireframe/Wireframe';

/**
 * シフト申請
 * @returns
 */
const ShiftApplication = () => {
  const { actions, state } = useGlobalStateMachine();
  const master = useMasterContext();
  if (
    TypeUtils.isNil(state.userInfo) ||
    (TypeUtils.isNilNumber(master.role.representative?.id)
      ? false
      : state.userInfo.role_id.includes(master.role.representative!.id!))
  ) {
    return (
      <Navigate
        to={pagePaths.ROOT}
        state={{ from: pagePaths.SHIFT_APPLICATION }}
      />
    );
  }
  const { toast } = useToastHook();

  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const {
    MonthChanger,
    setState,
    state: monthState,
  } = useDateChangers({
    initialState:
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.SHIFT_APPLICATION?.dateState,
  });
  const ref = React.useRef<FullCalendar>(null);
  const api = ref.current?.getApi();
  const { CheckArea, checked, off } = useCheckArea();

  const yearMonth = `${monthState.year}-${monthState.month
    .toString()
    .padStart(2, '0')}-01`;
  const postShiftApplySearch = usePostShiftApplySearch();
  const { data: deadlines, refetch: refetchDeadlines } = usePostDeadlineSearch({
    modes: [DeadlineMode.Shift],
  });

  const [events, setEvents] = React.useState<EventInput[]>([]);

  const postShiftApply = usePostShiftApply();

  const data = queryClient.getQueryData<ShiftApplySingleSearchResult>(
    SHIFT_APPLY_CONTEXT_NAMES.POST_SHIFT_APPLY_SINGLE_SEARCH
  );
  const setData = React.useCallback(
    (result: ShiftApplySingleSearchResult) =>
      queryClient.setQueryData(
        SHIFT_APPLY_CONTEXT_NAMES.POST_SHIFT_APPLY_SINGLE_SEARCH,
        result
      ),
    [queryClient]
  );
  const orderedAttendanceTypeId = React.useMemo(
    () =>
      Object.values(master.attendanceType)
        .filter((m) =>
          (
            ((state.userInfo?.roleOptions.isOutsourcer
              ? state.appConfig.facilitySettings
                  ?.SHIFT_APPLY_FOR_OUTSOURCER_ATTENDANCE_TYPE_IDS
              : state.appConfig.facilitySettings
                  ?.SHIFT_APPLY_ATTENDANCE_TYPE_IDS) as number[]) || []
          ).includes(m.id)
        )
        .sort(ListUtils.sort((m) => m.displayOrder))
        .map((m) => m.id),
    [master.attendanceType]
  );
  const getNextAttendanceTypeId = React.useCallback<
    FunctionType<number | undefined, number | undefined>
  >(
    (attendanceTypeId) => {
      if (TypeUtils.isNilNumber(attendanceTypeId)) {
        return orderedAttendanceTypeId.at(0);
      }
      const index = orderedAttendanceTypeId.indexOf(attendanceTypeId);
      if (index === -1) {
        return undefined;
      }
      const nextId = orderedAttendanceTypeId.at(index + 1);
      return nextId;
    },
    [orderedAttendanceTypeId]
  );
  const holiday = React.useMemo<number>(
    () =>
      state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_HOLIDAY as number,
    [state.appConfig.facilitySettings]
  );
  const leave = React.useMemo<number>(
    () =>
      state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WITH_PAY as number,
    [state.appConfig.facilitySettings]
  );
  const present = React.useMemo<number>(
    () => state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WORK as number,
    [state.appConfig.facilitySettings]
  );
  React.useEffect(
    () =>
      setEvents(
        (data?.result?.dailyShifts
          ?.map((dailyShift) => {
            const attendanceTypeId =
              dailyShift.attendanceTypeIdActual ||
              dailyShift.attendanceTypeIdApply;

            return TypeUtils.isNilNumber(attendanceTypeId)
              ? null
              : ({
                  id: dailyShift.date,
                  allDay: true,
                  title: master
                    .getBy(MasterKey.AttendanceTypes, attendanceTypeId)
                    ?.name?.charAt(0),
                  start: dailyShift.date,
                  backgroundColor: TypeUtils.isNilNumber(attendanceTypeId)
                    ? undefined
                    : attendanceTypeId === leave
                    ? 'green'
                    : attendanceTypeId === holiday
                    ? 'red'
                    : undefined,
                  extendedProps: {
                    id: dailyShift.dailyWorkId,
                    attendanceTypeId: attendanceTypeId,
                    version: dailyShift.dailyWorkVersion,
                  },
                } as EventInput);
          })
          .filter(TypeUtils.isNonNil) as EventInput[]) || []
      ),
    [data]
  );
  const onChangeChanger = (dateState: DateChangerState) => {
    actions.updatePageInfo({ SHIFT_APPLICATION: { dateState } });
    const applyUnitId = master.applyUnit.shiftApply?.id;
    off();
    if (TypeUtils.isNilNumber(applyUnitId)) {
      toast('申請単位：シフト申請がないため検索できません');
      return;
    }
    postShiftApplySearch.mutate(
      {
        year: dateState.year,
        month: dateState.month,
        applyUnitId: applyUnitId,
      },
      {
        onSuccess: (result) => {
          if (TypeUtils.isNonNil(result)) {
            setData(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError
              ? error
              : 'シフト申請情報の取得に失敗しました'
          ),
      }
    );
  };

  React.useState(() => {
    refetchDeadlines();
    onChangeChanger(monthState);
  });
  React.useEffect(() => {
    const newState: DateChangerState =
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.SHIFT_APPLICATION?.dateState;
    onChangeChanger(newState);
    setState(newState);
    api?.gotoDate(
      Formatter.date(new Date(newState.year, newState.month - 1, 1), true)
    );
  }, [searchParams]);

  return (
    <Wireframe
      title={pageNames.SHIFT_APPLICATION}
      topMessages={deadlines?.results}
    >
      <VStack width={sizes.width.styledMessageList}>
        <MonthChanger
          onChangeLeft={(s) => {
            onChangeChanger(s);
            api?.prev();
          }}
          onChangeRight={(s) => {
            onChangeChanger(s);
            api?.next();
          }}
        />
        {/* スケルトン入れるとカレンダーAPIが取得できなくなる (ref がとれない) */}
        {/* <Skeleton isLoaded={!postShiftApplySearch.isLoading} width="100%"> */}
        <Box backgroundColor="white" width="100%">
          <FullCalendar
            dateClick={(info) => {
              if (
                info.date < info.view.currentStart ||
                info.view.currentEnd <= info.date ||
                TypeUtils.isNonNilString(data?.result?.shiftApply?.approveTime)
              ) {
                return;
              }
              const date = Formatter.date(info.date, true);
              const thisEvent = events.find((event) => event.id === date);
              const nextAttendanceTypeId = getNextAttendanceTypeId(
                thisEvent?.extendedProps?.attendanceTypeId
              );
              if (TypeUtils.isNilNumber(nextAttendanceTypeId)) {
                // なし
                setEvents(events.filter((event) => event.id !== date));
              } else {
                console.log(nextAttendanceTypeId);
                setEvents(
                  events
                    .filter((event) => event.id !== date)
                    .concat({
                      ...thisEvent,
                      id: date,
                      title: master
                        .getBy(MasterKey.AttendanceTypes, nextAttendanceTypeId)
                        ?.name?.charAt(0),
                      start: date,
                      backgroundColor:
                        nextAttendanceTypeId === leave
                          ? 'green'
                          : nextAttendanceTypeId === holiday
                          ? 'red'
                          : undefined,
                      extendedProps: {
                        ...thisEvent?.extendedProps,
                        attendanceTypeId: nextAttendanceTypeId,
                      },
                    } as EventInput)
                );
              }
            }}
            dayCellContent={(args) => args.date.getDate()}
            defaultAllDay={true}
            eventClick={(info) => {
              if (
                (info.event.start &&
                  (info.event.start < info.view.currentStart ||
                    info.view.currentEnd <= info.event.start)) ||
                TypeUtils.isNonNilString(data?.result?.shiftApply?.approveTime)
              ) {
                return;
              }
              const nextAttendanceTypeId = getNextAttendanceTypeId(
                info.event.extendedProps?.attendanceTypeId
              );
              if (TypeUtils.isNilNumber(nextAttendanceTypeId)) {
                // なし
                setEvents(events.filter((event) => event.id !== info.event.id));
              } else {
                setEvents(
                  events
                    .filter((event) => event.id !== info.event.id)
                    .concat({
                      ...info.event,
                      id: info.event.id,
                      title: master
                        .getBy(MasterKey.AttendanceTypes, nextAttendanceTypeId)
                        ?.name?.charAt(0),
                      start: info.event.startStr,
                      backgroundColor:
                        nextAttendanceTypeId === leave
                          ? 'green'
                          : nextAttendanceTypeId === holiday
                          ? 'red'
                          : undefined,
                      extendedProps: {
                        ...info.event.extendedProps,
                        attendanceTypeId: nextAttendanceTypeId,
                      },
                    } as EventInput)
                );
              }
            }}
            events={events}
            headerToolbar={false}
            height="80vh"
            initialDate={yearMonth}
            initialView="dayGridMonth"
            locale={jaLocale}
            plugins={[dayGridPlugin, interactionPlugin]}
            ref={ref}
            timeZone="Asia/Tokyo"
          />
        </Box>
        <CheckArea
          button={DecideButton}
          buttonProps={{
            isDisabled:
              !checked ||
              TypeUtils.isNonNilString(data?.result?.shiftApply?.approveTime),
          }}
          confirmMessage="シフトを上記のとおり申請します"
          doneMessage={
            TypeUtils.isNonNilString(data?.result?.shiftApply?.approveTime)
              ? '承認済です'
              : undefined
          }
          onClick={() => {
            const dailyShifts: Array<DailyShift> = [];
            const eventDates = events
              .filter((e) => !Array.isArray(e.start))
              .map((e) =>
                Formatter.date(e.start as Date | string | number | undefined)
              );
            const lastDay = Formatter.createDate(yearMonth);
            lastDay.setMonth(lastDay.getMonth() + 1);
            lastDay.setDate(0);
            for (
              const d = Formatter.createDate(yearMonth);
              d.valueOf() <= lastDay.valueOf();
              d.setDate(d.getDate() + 1)
            ) {
              if (!eventDates.includes(Formatter.date(d))) {
                dailyShifts.push({
                  date: Formatter.date(d, true),
                  userCodeWorker: state.userInfo?.user_code,
                  attendanceTypeIdApply: present,
                });
              }
            }
            dailyShifts.push(
              ...events
                .filter((e) => !Array.isArray(e.start))
                .map((event) => {
                  // let attendanceTypeId: number | undefined = 0;
                  // if (event.title === '休') {
                  //   attendanceTypeId = master.attendanceType.holiday?.id;
                  // } else if (event.title === '有') {
                  //   attendanceTypeId = master.attendanceType.leave?.id;
                  // } else if (event.title === '出') {
                  //   attendanceTypeId = master.attendanceType.present?.id;
                  // }

                  return {
                    date: Formatter.date(
                      event.start as Date | string | number | undefined,
                      true
                    ),
                    userCodeWorker: state.userInfo?.user_code,
                    dailyWorkId: event.extendedProps?.id,
                    dailyWorkVersion: event.extendedProps?.version,
                    attendanceTypeIdApply:
                      event.extendedProps?.attendanceTypeId,
                  };
                })
            );
            actions.setGuard(true);
            postShiftApply.mutate(
              {
                applyUnitId: master.applyUnit.shiftApply?.id,
                dailyShifts: dailyShifts,
                userCodeWorker: state.userInfo?.user_code,
                yearMonth,
              },
              {
                onSuccess: () => {
                  refetchDeadlines();
                  navigate();
                  actions.setGuard(false);
                },
                onError: (error) => {
                  toast(
                    error instanceof AppError
                      ? error
                      : 'シフト申請に失敗しました'
                  );
                  actions.setGuard(false);
                },
              }
            );
          }}
        />
        {/* </Skeleton> */}
        <HStack width="100%">
          <BackButton onClick={() => navigate(-1)} />
        </HStack>
      </VStack>
    </Wireframe>
  );
};

export default ShiftApplication;
